/**
 * Deterministic render counting for the table component tree.
 *
 * Wall-clock timings are too noisy to assert on in CI, but the number of times each table component
 * renders in response to a given interaction is deterministic, so it can be asserted exactly. The
 * render-count suite in `core-ui/__tests__/perf/` turns counting on, drives a scenario, and compares
 * the counts against committed baselines.
 *
 * Counting is off unless a test turns it on, so the production render path pays for one comparison
 * against a module-level null.
 */

/**
 * The components whose renders are counted. Anything that renders once per cell or per row belongs
 * here; the whole point is to notice when a single-cell interaction re-renders the whole table.
 */
export const INSTRUMENTED_TABLE_COMPONENTS = [
  'TableEditableClass',
  'TableEditableComponent',
  'TableRow',
  'TableCell',
  'TableAxisCell',
  'TableHeaderRow',
  'TableCellEditor',
] as const;

export type InstrumentedTableComponent = (typeof INSTRUMENTED_TABLE_COMPONENTS)[number];

export type TableRenderCounts = Record<InstrumentedTableComponent, number>;

function zeroedCounts(): TableRenderCounts {
  const counts = {} as TableRenderCounts;
  for (const component of INSTRUMENTED_TABLE_COMPONENTS) {
    counts[component] = 0;
  }
  return counts;
}

let counts: TableRenderCounts | null = null;

/** Begin counting renders, discarding any counts from a previous scenario. */
export function startCountingTableRenders(): void {
  counts = zeroedCounts();
}

/** Stop counting renders. Safe to call when counting was never started. */
export function stopCountingTableRenders(): void {
  counts = null;
}

/**
 * Zero the counters while leaving counting on, so a scenario can measure an interaction without the
 * mount that preceded it.
 */
export function resetTableRenderCounts(): void {
  if (counts != null) {
    counts = zeroedCounts();
  }
}

export function readTableRenderCounts(): TableRenderCounts {
  if (counts == null) {
    throw new Error('Table render counting is not active. Call startCountingTableRenders() first.');
  }
  return {...counts};
}

/**
 * Record one render of `component`. Called from the component's render body, so it runs once per
 * render attempt regardless of whether React commits the result.
 */
export function countTableRender(component: InstrumentedTableComponent): void {
  if (counts != null) {
    counts[component] += 1;
  }
}
